"use client";

import { useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  // HD images of artisanal miners from Congo DRC
  const minerImages = [
    {
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_1eee10751-1768560521489.png",
      alt: 'Artisanal miners working in Congo DRC mineral extraction site'
    },
    {
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_104055887-1770448089440.png",
      alt: 'Congo DRC artisanal mining community extracting precious minerals'
    },
    {
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_13fee596d-1766766162492.png",
      alt: 'Traditional artisanal mining techniques in Congo DRC'
    },
    {
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_1eee10751-1768560521489.png",
      alt: 'Congo DRC miners processing mineral deposits'
    }
  ];

  useEffect(() => {
    // Auto-rotate slideshow every 5 seconds
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % minerImages?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [minerImages?.length]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Animated Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-amber-600/10 animate-pulse" />
      {/* Parallax Background Image */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{ transform: `translateY(${scrollY * 0.5}px)` }}
      >
        <AppImage
          src="https://images.unsplash.com/photo-1726554210101-950c365ca9b4"
          alt="African mining landscape with gold deposits and mineral excavation"
          className="w-full h-full object-cover"
          priority
        />
      </div>
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />
      {/* Content Container */}
      <div className="relative z-10 container mx-auto px-6 py-32 min-h-screen flex items-center">
        <div className="grid lg:grid-cols-2 gap-16 items-center w-full">
          
          {/* Left Content */}
          <div className="space-y-8 text-white">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 backdrop-blur-sm border border-amber-500/30 rounded-full px-6 py-2">
              <Icon name="SparklesIcon" size={20} className="text-amber-400" />
              <span className="text-sm font-medium text-amber-200">Premium Congo DRC Minerals</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold leading-tight">
              <span className="bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 bg-clip-text text-transparent">
                Ethical Mining,
              </span>
              <br />
              <span className="text-white">Global Excellence</span>
            </h1>

            <p className="text-xl text-slate-300 leading-relaxed max-w-xl">
              Premium quality minerals ethically sourced from Congo DRC's richest deposits. 
              Supporting artisanal miners with fair trade practices and full legal compliance.
            </p>

            {/* Stats Row */}
            <div className="flex flex-wrap gap-8 pt-4">
              <div>
                <p className="text-4xl font-bold text-amber-400">500+</p>
                <p className="text-sm text-slate-400">kg Gold Exported</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-amber-400">15+</p>
                <p className="text-sm text-slate-400">Countries Served</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-amber-400">99.9%</p>
                <p className="text-sm text-slate-400">Purity Guarantee</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <Link 
                href="/contact" 
                className="group relative inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold px-8 py-4 rounded-full transition-all shadow-lg shadow-amber-500/30 hover:shadow-xl hover:shadow-amber-500/50 hover:scale-105"
              >
                Request Quote
                <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link 
                href="/products" 
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 text-white font-bold px-8 py-4 rounded-full transition-all"
              >
                View Products
              </Link>
            </div>
          </div>

          {/* Right Content - Image Slideshow */}
          <div className="relative">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl shadow-black/50">
              {/* Slideshow Images */}
              {minerImages?.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <AppImage
                    src={image?.src}
                    alt={image?.alt}
                    className="w-full h-full object-cover"
                    priority={index === 0}
                  />
                </div>
              ))}

              {/* Gradient Overlay on Image */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

              {/* Image Caption */}
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <p className="text-sm font-medium text-amber-400 mb-2">Congo DRC Artisanal Mining</p>
                <p className="text-lg font-semibold">Supporting Local Communities</p>
              </div>

              {/* Slideshow Indicators */}
              <div className="absolute bottom-8 right-8 flex gap-2">
                {minerImages?.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-1.5 rounded-full transition-all ${
                      index === currentSlide 
                        ? 'bg-amber-400 w-8' :'bg-white/40 w-1.5 hover:bg-white/60'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Floating Badge */}
            <div className="absolute -top-6 -right-6 bg-gradient-to-br from-amber-400 to-amber-600 text-white rounded-2xl p-6 shadow-xl shadow-amber-500/30 rotate-3 hover:rotate-0 transition-transform">
              <Icon name="ShieldCheckIcon" size={32} className="mb-2" />
              <p className="font-bold text-sm">ISO Certified</p>
              <p className="text-xs opacity-90">Ethical Sourcing</p>
            </div>
          </div>
        </div>
      </div>
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-bounce">
        <Icon name="ChevronDownIcon" size={32} className="text-white/60" />
      </div>
    </section>
  );
}